<script setup lang="ts">
interface Props {
  rating: number
  maxRating?: number
  showValue?: boolean
  size?: 'sm' | 'md' | 'lg'
  color?: string
  textColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  maxRating: 5,
  showValue: false,
  size: 'md',
  color: 'text-oxo-red',
  textColor: undefined
})

const safeRating = computed(() => isNaN(props.rating) ? 0 : props.rating)
const fullStars = computed(() => Math.floor(safeRating.value))
const hasHalfStar = computed(() => safeRating.value % 1 >= 0.25 && safeRating.value % 1 < 0.75)
const fullStarsAdjusted = computed(() => safeRating.value % 1 >= 0.75 ? fullStars.value + 1 : fullStars.value)
const emptyStars = computed(() => Math.max(0, props.maxRating - fullStarsAdjusted.value - (hasHalfStar.value ? 1 : 0)))
</script>

<template>
  <div class="flex items-center gap-2">
    <div class="flex items-center gap-0.5" :class="color">
      <!-- Full Stars -->
      <i v-for="i in fullStarsAdjusted" :key="`full-${i}`"
        :class="['fas fa-star', size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-6 h-6']"
        aria-hidden="true"></i>

      <!-- Half Star -->
      <i v-if="hasHalfStar"
        :class="['fas fa-star-half-alt', size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-6 h-6']"
        aria-hidden="true"></i>

      <!-- Empty Stars -->
      <i v-for="i in emptyStars" :key="`empty-${i}`"
        :class="['far fa-star opacity-20', size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-6 h-6']"
        aria-hidden="true"></i>
    </div>

    <span v-if="showValue" class="font-display font-black" :class="[
      size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-xl',
      textColor || color
    ]">
      {{ safeRating.toFixed(1) }} <span class="text-foreground-dim font-bold">/ {{ maxRating }}</span>
    </span>
  </div>
</template>
